import { AnimatePresence, motion } from 'motion/react';
import styles from './NoiseMeter.module.css';

import content_green from '../../assets/images/content_green.png';
import content_yellow from '../../assets/images/content_yellow.png';
import content_red from '../../assets/images/content_red.png';
import arrow from '../../assets/images/arrow.png';

const calculateOpacity = (
  current,
  min,
  max,
  opacityMin = 0.2,
  opacityMax = 0.5
) => {
  return (
    ((current - min) / (max - min)) * (opacityMax - opacityMin) + opacityMin
  );
};

const NoiseMeter = ({
  noiseLevel = 0,
  prevRecord = 80,
  maxNoiseLevel = 120,
  visibleRecord,
}) => {
  const needleRotation = Math.min((noiseLevel / maxNoiseLevel) * 180 - 90, 90);
  const percentMask = (noiseLevel / maxNoiseLevel) * 50;

  const prevRecordDeg =
    ((prevRecord - maxNoiseLevel / 2) / (maxNoiseLevel / 2)) * 90;

  const prevRecordTextDeg = prevRecordDeg > 0 ? 23 : prevRecordDeg;
  const prevRecordTextX = prevRecordDeg > 0 ? 10 : -10;

  const majorTicks = Array.from({ length: 30 }, (_, i) => i * 12);
  const minorTicks = Array.from({ length: 60 }, (_, i) => i * 6);

  const centerX = 300;
  const centerY = 300;
  const radius = 225;

  const greenOpacity =
    needleRotation <= -30
      ? calculateOpacity(needleRotation, -90, -30, 0.4, 0.8)
      : 0.8;
  const yellowOpacity =
    needleRotation <= -30
      ? 0.4
      : needleRotation > -30 && needleRotation <= 30
      ? calculateOpacity(needleRotation, -30, 30, 0.4, 0.9)
      : 0.9;

  const redOpacity =
    needleRotation <= 30
      ? 0.4
      : needleRotation > 30 && needleRotation <= 90
      ? calculateOpacity(needleRotation, 30, 90, 0.4, 0.9)
      : 0.9;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className={styles.noiseMeterWrapper}
    >
      <AnimatePresence>
        {prevRecord && visibleRecord && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            exit={{ opacity: 0 }}
            className={styles.prevRecordBlock}
            style={{
              transform: `rotate(${prevRecordDeg}deg) translateY(-38rem) `,
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {prevRecord && visibleRecord && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            exit={{ opacity: 0 }}
            className={styles.prevRecord}
            style={{
              transform: `rotate(${prevRecordTextDeg}deg) translateY(-48rem)`,
            }}
          >
            <div
              style={{
                transform: `rotate(${-prevRecordTextDeg}deg)  translateX(${prevRecordTextX}rem)`,
              }}
            >
              <span className={styles.prevText}>рекорд:</span>
              <span className={styles.prevValue}>{prevRecord}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.noiseMeter}>
        <div className={styles.circles}>
          <svg viewBox="0 0 600 600" className={styles.mainCircle}>
            <circle
              cx="50%"
              cy="50%"
              r="49%"
              fill="transparent"
              stroke="rgba(45, 188, 255, 1)"
              strokeWidth="3"
              className={styles.circleGlow}
            />

            {majorTicks.map((angle) => (
              <line
                key={`major-${angle}`}
                x1={centerX}
                y1={centerY - radius + 10}
                x2={centerX}
                y2={centerY - radius + 50}
                stroke="#2DBCFF"
                strokeWidth="2"
                transform={`rotate(${angle} ${centerX} ${centerY})`}
              />
            ))}

            {minorTicks.map((angle) => (
              <line
                key={`minor-${angle}`}
                x1={centerX}
                y1={centerY - radius + 20}
                x2={centerX}
                y2={centerY - radius + 40}
                stroke="#2DBCFF"
                strokeWidth="4"
                className={styles.tickGlow}
                transform={`rotate(${angle} ${centerX} ${centerY})`}
              />
            ))}
          </svg>

          {/* <div className={styles.shadowTicks} /> */}

          <div
            className={styles.sectors}
            style={{
              '--greenOpacity': greenOpacity,
              '--yellowOpacity': yellowOpacity,
              '--redOpacity': redOpacity,
            }}
          >
            <motion.img
              initial={{ scale: 0 }}
              animate={{ scale: needleRotation > -90 ? [0, 1.1, 1] : 0 }}
              transition={{ duration: 0.3 }}
              src={content_green}
              alt="like"
              className={styles.content_green}
            />
            <motion.img
              initial={{ scale: 0 }}
              animate={{ scale: needleRotation > -29 ? [0, 1.1, 1] : 0 }}
              transition={{ duration: 0.3 }}
              src={content_yellow}
              alt="hand"
              className={styles.content_yellow}
            />
            <motion.img
              initial={{ scale: 0 }}
              animate={{ scale: needleRotation > 32 ? [0, 1.1, 1] : 0 }}
              transition={{ duration: 0.3 }}
              src={content_red}
              alt="like"
              className={styles.content_red}
            />
          </div>

          <div className={styles.smallCircle} />

          <div className={styles.circleDash}>
            <svg width="200" height="200" viewBox="0 0 200 200">
              <defs>
                <linearGradient
                  id="gradient-arc"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="white" stopOpacity="0" />{' '}
                  <stop offset="10%" stopColor="white" stopOpacity="0.3" />{' '}
                  <stop offset="50%" stopColor="white" stopOpacity="1" />{' '}
                  <stop offset="90%" stopColor="white" stopOpacity="0.3" />{' '}
                  <stop offset="100%" stopColor="white" stopOpacity="0" />{' '}
                </linearGradient>
              </defs>

              <circle
                cx="100"
                cy="100"
                r="40"
                stroke="url(#gradient-arc)"
                strokeWidth="5"
                fill="none"
                strokeDasharray="125.6,125.6"
              />
            </svg>
          </div>

          {/* <div
            className={styles.gradient}
            style={{
              '--percentMask': `${percentMask}%`,
              '--percentMask1': `${percentMask + 0.5}%`,
              '--needleRotation': `${needleRotation}deg`,
            }}
          /> */}

          <div className={styles.minLevel}>0</div>
          <div className={styles.mediumLevel}>
            {Math.round(maxNoiseLevel / 2)}
          </div>
          <div className={styles.maxLevel}>{maxNoiseLevel}</div>

          <div
            className={styles.line}
            style={{
              '--needleRotation': `${needleRotation}deg`,
            }}
          >
            <img src={arrow} alt="arrow" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NoiseMeter;
